/**
 * CV Match Category Utilities
 * 
 * Shared utilities for mapping backend category keys to frontend display names
 * and filtering evidence categories based on search criteria.
 */

// Map backend category keys to frontend display names and evidence fields
export const CATEGORY_MAPPING = {
    'geographic_countries': { title: 'Geographic Experience', field: 'geographic_evidence' },
    'geographic_regions': { title: 'Geographic Experience', field: 'geographic_evidence' },
    'methods': { title: 'Methods & Methodologies', field: 'methodology_evidence' },
    'sectors': { title: 'Sectors', field: 'sector_evidence' },
    'technical_domain': { title: 'Sectors', field: 'sector_evidence' },
    'tools': { title: 'Technical Tools', field: 'technical_tools_evidence' },
    'frameworks': { title: 'Frameworks', field: 'framework_evidence' },
    'languages': { title: 'Languages', field: 'language_evidence' },
    'donors': { title: 'Donor/Client Experience', field: 'client_type_evidence' },
    'education_level': { title: 'Education', field: 'education_evidence' },
    'education_fields': { title: 'Education', field: 'education_evidence' },
    'experience_years': { title: 'Experience Level', field: 'role_level_evidence' },
    'experience_types': { title: 'Experience Level', field: 'role_level_evidence' },
    'communication': { title: 'Methods & Methodologies', field: 'methodology_evidence' },
    'professional_ethical': { title: 'Methods & Methodologies', field: 'methodology_evidence' }
} as const;

// Type for category mapping entries
type CategoryMappingEntry = typeof CATEGORY_MAPPING[keyof typeof CATEGORY_MAPPING];
type CategoryField = CategoryMappingEntry['field'];
type CategoryTitle = CategoryMappingEntry['title'];

/**
 * Get filtered evidence categories based on selected search categories
 * @param selectedCategories - Array of category keys that were searched
 * @returns Array of { title, field } objects for rendering
 */
export function getFilteredEvidenceCategories(selectedCategories?: string[]): Array<{ title: string; field: string }> {
    if (!selectedCategories || selectedCategories.length === 0) {
        // If no selected categories, show all (fallback behavior)
        return [
            { title: 'Geographic Experience', field: 'geographic_evidence' },
            { title: 'Methods & Methodologies', field: 'methodology_evidence' },
            { title: 'Sectors', field: 'sector_evidence' },
            { title: 'Technical Tools', field: 'technical_tools_evidence' },
            { title: 'Frameworks', field: 'framework_evidence' },
            { title: 'Languages', field: 'language_evidence' },
            { title: 'Donor/Client Experience', field: 'client_type_evidence' },
            { title: 'Education', field: 'education_evidence' },
            { title: 'Experience Level', field: 'role_level_evidence' }
        ];
    }
    
    // Group selected categories by their display field
    const fieldGroups = new Map<string, string>();
    selectedCategories.forEach(catKey => {
        const mapping = CATEGORY_MAPPING[catKey as keyof typeof CATEGORY_MAPPING];
        if (mapping) {
            fieldGroups.set(mapping.field, mapping.title);
        }
    });
    
    // Convert to array format
    return Array.from(fieldGroups.entries()).map(([field, title]) => ({ title, field }));
}

/**
 * Type-safe function to get category mapping
 * @param catKey - Backend category key
 * @returns Category mapping entry or undefined
 */
export function getCategoryMapping(catKey: string): CategoryMappingEntry | undefined {
    return CATEGORY_MAPPING[catKey as keyof typeof CATEGORY_MAPPING];
}
